import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { BASE_URL, studyCamelCaseToSpacedCapitalizedMap, studySpacedCapitalizedToCamelCaseMap } from '../../utils/constants';
import { Table, Button, Form, Accordion } from 'react-bootstrap'
import { Oval } from "react-loader-spinner"
import Modal from 'react-bootstrap/Modal'
import { useNavigate,} from 'react-router-dom';
import { FaChartLine, FaPlus } from "react-icons/fa6";
import { Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, LabelList, ReferenceLine } from 'recharts';


import PatientInformationSurgicalTimeAndPersonnel from '../../components/ArthrexCatalog/General/PatientInformationSurgicalTimeAndPersonnel/PatientInformationSurgicalTimeAndPersonnel'
import HandDominance from '../../components/ArthrexCatalog/General/HandDominance/HandDominance';
import Diagnosis from '../../components/ArthrexCatalog/General/Diagnosis/Diagnosis';
import Operations from '../../components/ArthrexCatalog/General/Operations/Operations';

import OperationsLabrumAndInstability from '../../components/ArthrexCatalog/General/OperationsLabrumAndInstability/OperationsLabrumAndInstability';
import Implants from '../../components/ArthrexCatalog/General/Implants/Implants';
import SurgicalDressing from '../../components/ArthrexCatalog/General/SurgicalDressing/SurgicalDressing';
import Grafts from '../../components/ArthrexCatalog/General/Grafts/Grafts';
import OperationsAndImplants from '../../components/ArthrexCatalog/ShoulderArthroplasty/OperationsAndImplants/OperationsAndImplants';
import PostOperativeManagement from '../../components/ArthrexCatalog/ShoulderArthroplasty/PostOperativeManagement/PostOperativeManagement';

import { camelCaseToReadable, getCatalogEntryData, getRandomColor } from '../../utils/fuctions';

import "./Analysis.scss"

// Color palette matching the application's category colors
const CHART_COLORS = [
  '#059669', // Teal/Emerald (primary)
  '#d55846', // Coral/Red (diagnosis)
  '#55ab67', // Green (operations)
  '#567eb4', // Blue (grafts)
  '#8548a7', // Purple (implants)
  '#58ba9d', // Teal (surgical dressing)
  '#394958', // Dark blue-gray
  '#dc3545', // Red (complications)
  '#007bff', // Primary blue
  '#4caeff', // Light blue
]

// Get chart color ensuring no duplicates within the same chart
const getChartColor = (index) => CHART_COLORS[index % CHART_COLORS.length]

// Responsive chart styling configurations
const getCommonChartProps = () => {
  const isMobile = window.innerWidth <= 768;
  return {
    margin: {
      top: isMobile ? 15 : 25,
      right: isMobile ? 20 : 40,
      left: isMobile ? 15 : 25,
      bottom: isMobile ? 80 : 70,
    },
    overflow: "visible"
  };
}

// Responsive legend props
const getResponsiveLegendProps = () => {
  const isMobile = window.innerWidth <= 768;
  return {
    wrapperStyle: {
      paddingTop: isMobile ? '20px' : '30px',
      fontSize: isMobile ? '11px' : '13px',
      fontWeight: 600
    },
    verticalAlign: "top",
    align: "center",
    layout: isMobile ? "vertical" : "horizontal"
  };
}

// Responsive axis props
const getResponsiveXAxisProps = (dataKey = "stage") => {
  const isMobile = window.innerWidth <= 768;
  return {
    dataKey,
    angle: isMobile ? 90 : 45,
    textAnchor: "start",
    interval: 0,
    allowDuplicatedCategory: false,
    dy: 10,
    tick: { fill: '#718096', fontSize: isMobile ? 10 : 12, fontWeight: 500 },
    axisLine: { stroke: '#e2e8f0', strokeWidth: 1 },
    tickLine: { stroke: '#e2e8f0', strokeWidth: 1 },
    height: isMobile ? 80 : 60
  };
}

const getCommonGridProps = () => ({
  strokeDasharray: "4 4",
  stroke: "#f0f4f8",
  strokeWidth: 1
})

const getCommonAxisProps = () => ({
  tick: { fill: '#718096', fontSize: 12, fontWeight: 500 },
  axisLine: { stroke: '#e2e8f0', strokeWidth: 1 },
  tickLine: { stroke: '#e2e8f0', strokeWidth: 1 }
})

const getCommonTooltipProps = () => ({
  contentStyle: {
    backgroundColor: 'white',
    border: 'none',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.16)',
    padding: '16px'
  },
  labelStyle: { color: '#2d3748', fontWeight: 600, marginBottom: '8px' }
})

const getCommonLegendProps = () => ({
  wrapperStyle: {
    paddingTop: '30px',
    fontSize: '13px',
    fontWeight: 600
  },
  verticalAlign: "top",
  align: "center"
})

// Custom enhanced tooltip component
const CustomTooltip = ({ active, payload, label, chartType, coordinate }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <div className="tooltip-header">
          <h4 className="tooltip-title">{label}</h4>
        </div>
        <div className="tooltip-content">
          {payload.map((entry, index) => (
            <div key={index} className="tooltip-item">
              <div className="tooltip-item-header">
                <div
                  className="tooltip-color-indicator"
                  style={{ backgroundColor: entry.color }}
                ></div>
                <span className="tooltip-group-name">{entry.name}</span>
              </div>
              <div className="tooltip-value">
                <span className="value-number">{entry.value}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

// Score interpretation helper
const getScoreInterpretation = (value, chartType) => {
  if (chartType.includes('VAS')) {
    if (value <= 2) return 'Minimal Pain';
    if (value <= 4) return 'Mild Pain';
    if (value <= 6) return 'Moderate Pain';
    if (value <= 8) return 'Severe Pain';
    return 'Extreme Pain';
  }
  if (chartType.includes('SANE')) {
    if (value >= 90) return 'Excellent';
    if (value >= 75) return 'Good';
    if (value >= 60) return 'Fair';
    return 'Poor';
  }
  if (chartType.includes('ASES')) {
    const isIndex = chartType.includes('Index');
    const threshold = isIndex ? [90, 75, 60] : chartType.includes('Elbow') ? [32, 28, 24] : [27, 22, 18];
    if (value >= threshold[0]) return 'Excellent';
    if (value >= threshold[1]) return 'Good';
    if (value >= threshold[2]) return 'Fair';
    return 'Poor';
  }
  return '';
};

// Improved custom label function with intelligent placement
const CustomLabel = (props) => {
  const { x, y, value, height, style, index, payload } = props;

  // Skip rendering if value is null or undefined
  if (value == null) return null;

  // Calculate intelligent label position based on chart area and value position
  const chartHeight = height || 400;
  const relativeY = y / chartHeight;

  // Determine optimal position based on chart location
  let dy = -15; // Default above
  let textAnchor = "middle";

  // If in top 30% of chart, place label below the point
  if (relativeY < 0.3) {
    dy = 25;
  }
  // If in bottom 30% of chart, place label above the point
  else if (relativeY > 0.7) {
    dy = -15;
  }
  // Middle area - alternate above/below based on index to reduce overlap
  else {
    dy = (index % 2 === 0) ? -15 : 25;
  }

  return (
    <text
      x={x}
      y={y}
      dy={dy}
      fill={style.fill}
      textAnchor={textAnchor}
      fontSize="11"
      fontWeight="600"
      className="chart-label"
    >
      {value}
    </text>
  );
};

// Helper function to wrap LabelList with custom styling
const renderCustomLabel = (height, style) => (props) => (
  <CustomLabel
    {...props}
    height={height}
    style={style}
  />
);

const Analysis = ({ user, logUserOut }) => {
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false);
  const [study, setStudy] = useState("");
  const [numberOfGroups, setNumberOfGroups] = useState(0)
  const [patientGroups, setPatientGroups] = useState([])
  const [groupName, setGroupName] = useState(`Group ${numberOfGroups + 1}`);
  const [cases, setCases] = useState(0);
  const [editingGroupIndex, setEditingGroupIndex] = useState(null);
  const [catalogEntries, setCatalogEntries] = useState([])
  const [sorting, setSorting] = useState({ field: 'alertDate', ascending: true })
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [activeAccordionIndex, setActiveAccordionIndex] = useState(null);

  const handleAccordionChange = (form, currentIndex) => {
    if (activeAccordion !== form) {
      // Opening a new accordion, store its index
      setActiveAccordionIndex(currentIndex);
    } else {
      // Toggling closed
      setActiveAccordionIndex(null);
    }
    setActiveAccordion(activeAccordion === form ? null : form);
  };

  const navigate = useNavigate();
  const config = {
    headers: {
      Authorization: `Bearer ${user.token}`,
    },
  }

  useEffect(() => {
    const resetCatalogEntries = async () => {
      setCatalogEntries([])
    }
    if(editingGroupIndex === null) {
      resetCatalogEntries()
    }
  }, [study])
  

  useEffect(() => {
    const getAnalysisGroupCasesCount = async () => {
      try {
        const body = {
          study: study,
          catalogEntries: catalogEntries
        }
        const response = await axios.post(`${BASE_URL}/api/v1/analysis/cases-count`, body, config)
        setCases(response.data.casesCount)
      }
      catch (error) {
        console.log(error)
        const message = error.response && error.response.data.message ? error.response.data.message : error.message
        if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
          logUserOut()
        }
      }
    }
    getAnalysisGroupCasesCount()
  }, [catalogEntries])

  useEffect(() => {
    const resetGroupCreation = async () => {
      setCatalogEntries([])
      setGroupName(`Group ${numberOfGroups + 1}`)
      setStudy("")
      setCases(0)
    }
    if(editingGroupIndex === null) {
      resetGroupCreation()
    }
  }, [showModal])

  const editPatientGroupHandler = async (groupIndex) => {
    const groupToEdit = patientGroups[groupIndex];

    setEditingGroupIndex(groupIndex); // Set this before showing the modal
    setStudy(studySpacedCapitalizedToCamelCaseMap[groupToEdit.study]);
    setGroupName(groupToEdit.groupName);
    setCatalogEntries(groupToEdit.catalogEntries);
    setCases(groupToEdit.cases);
    setShowModal(true)
  };

  const savePatientGroupHandler = async () => {
    const { numberOfCases, analysisMetrics } = await getAnalysisGroupMetrics();
    const color = getChartColor(patientGroups.length);
    const newGroup = {
      groupName: groupName,
      catalogEntries: catalogEntries,
      study: studyCamelCaseToSpacedCapitalizedMap[study],
      cases: numberOfCases,
      metrics: analysisMetrics,
      color: color
    };

    setPatientGroups(prevState => {
      if (editingGroupIndex !== null) {
        const updatedGroups = [...prevState];
        updatedGroups[editingGroupIndex] = newGroup;
        return updatedGroups;
      }
      return [...prevState, newGroup];
    });

    setNumberOfGroups(prevState => editingGroupIndex !== null ? prevState : prevState + 1);
    setShowModal(false);
    setEditingGroupIndex(null);
  };

  const getAnalysisGroupMetrics = async () => {
    try {
      const body = {
        study: study,
        catalogEntries: catalogEntries
      }
      const response = await axios.post(`${BASE_URL}/api/v1/analysis/analysis-metrics`, body, config)
      return response.data
    }
    catch (error) {
      console.log(error)
      const message = error.response && error.response.data.message ? error.response.data.message : error.message
      if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
        logUserOut()
      }
    }
  }
  
  const addCatalogEntry = (category, form) => {
    const entryAlreadyExists = catalogEntries.some(entry => entry.category === category && entry.form === form);
    if (!entryAlreadyExists) {
      const newEntry = getCatalogEntryData(category, form);
      newEntry.filterType = 'include'; // Default to 'include'
      setCatalogEntries([...catalogEntries, newEntry]);
    }
  };

  const handleFilterTypeChange = (index, type) => {
    const updatedEntries = [...catalogEntries];
    updatedEntries[index].filterType = type;
    setCatalogEntries(updatedEntries);
  };

  const handleCatalogEntryFormDataUpdate = (arrayIndex, key, value) => {
    let newCatalogEntries = [...catalogEntries]
    newCatalogEntries[arrayIndex].formData[key] = value
    setCatalogEntries(newCatalogEntries)
  }

  const handleCatalogEntryRemoval = (arrayIndex) => {
    setCatalogEntries(catalogEntries.filter((catalogEntry, index) => index !== arrayIndex))
  }

  const renderCatalog = () => {
    switch(study) {
      case "shoulderArthroscopy": {
        return (
          <>
            <Accordion.Item eventKey="3" className="diagnosis">
              <Accordion.Header>Diagnosis</Accordion.Header>
              <Accordion.Body>
                <button onClick={() => addCatalogEntry("diagnosis", "rotatorCuff")}>Rotator Cuff</button>
                <button onClick={() => addCatalogEntry("diagnosis", "subacromialImpingement")}>Subacromial Impingement</button>
                <button onClick={() => addCatalogEntry("diagnosis", "labralInjuryAndInstability")}>Labral injury and instability</button>
                <button onClick={() => addCatalogEntry("diagnosis", "biceps")}>Biceps</button>
                <button onClick={() => addCatalogEntry("diagnosis", "acJoint")}>AC joint</button>
                <button onClick={() => addCatalogEntry("diagnosis", "osAcromiale")}>Os acriomiale</button>
                <button onClick={() => addCatalogEntry("diagnosis", "glenohumeralArthritis")}>Glenohumeral arthritis</button>
                <button onClick={() => addCatalogEntry("diagnosis", "articularCartilageOsteochondralLesion")}>Articular cartilage/osteochondral lesion</button>
                <button onClick={() => addCatalogEntry("diagnosis", "otherIntraArticular")}>Other intra-articular</button>
                <button onClick={() => addCatalogEntry("diagnosis", "fracture")}>Fracture</button>
                <button onClick={() => addCatalogEntry("diagnosis", "miscellaneous")}>Miscellaneous</button>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="4" className="operations">
              <Accordion.Header>Operations</Accordion.Header>
              <Accordion.Body>
                <button onClick={() => addCatalogEntry("operations", "rotatorCuffCompleteTear")}>Rotator cuff (complete tear)</button>
                <button onClick={() => addCatalogEntry("operations", "rotatorCuffPartialTear")}>Rotator cuff (partial tear)</button>
                <button onClick={() => addCatalogEntry("operations", "superiorCapsuleReconstruction")}>Superior Capsule Reconstruction (SCR)</button>
                <button onClick={() => addCatalogEntry("operations", "subscap")}>Subscap</button>
                <button onClick={() => addCatalogEntry("operations", "subAcromialDecompression")}>Subacromial decompression</button>
                <button onClick={() => addCatalogEntry("operations", "tendonTransfer")}>Tendon transfer</button>
                <button onClick={() => addCatalogEntry("operations", "biceps")}>Biceps</button>
                <button onClick={() => addCatalogEntry("operations", "coracoplasty")}>Coracoplasty</button>
                <button onClick={() => addCatalogEntry("operations", "distalClavicleExcision")}>Distal clavicle excision</button>
                <button onClick={() => addCatalogEntry("operations", "acJointStabilization")}>AC joint stabilization</button>
                <button onClick={() => addCatalogEntry("operations", "osAcromiale")}>Os acromiale</button>
                <button onClick={() => addCatalogEntry("operations", "osteotomy")}>Osteotomy</button>
                <button onClick={() => addCatalogEntry("operations", "cartilageTreatment")}>Cartilage treatment</button>
                <button onClick={() => addCatalogEntry("operations", "osteochondralTreatment")}>Osteochondral treatment</button>
                <button onClick={() => addCatalogEntry("operations", "otherIntraArticular")}>Other intra-articular</button>
                <button onClick={() => addCatalogEntry("operations", "fractureManagement")}>Fracture management (ORIF)</button>
                <button onClick={() => addCatalogEntry("operations", "miscellaneous")}>Miscellaneous</button>
              </Accordion.Body>

            </Accordion.Item>
            <Accordion.Item eventKey="5" className="operations-labrum-and-instability">
              <Accordion.Header>Operations - labrum & instability</Accordion.Header>
              <Accordion.Body>
                <button onClick={() => addCatalogEntry("operationsLabrumAndInstability", "slap")}>SLAP</button>
                <button onClick={() => addCatalogEntry("operationsLabrumAndInstability", "labralRepair")}>Labral repair</button>
                <button onClick={() => addCatalogEntry("operationsLabrumAndInstability", "capsulorrhaphy")}>Capsulorrhaphy</button>
                <button onClick={() => addCatalogEntry("operationsLabrumAndInstability", "haglRepair")}>HAGL repair</button>
                <button onClick={() => addCatalogEntry("operationsLabrumAndInstability", "alpsaRepair")}>ALPSA repair</button>
                <button onClick={() => addCatalogEntry("operationsLabrumAndInstability", "remplissage")}>Remplissage</button>
                <button onClick={() => addCatalogEntry("operationsLabrumAndInstability", "subscapularisAugmentation")}>Subscapularis augmentation</button>
                <button onClick={() => addCatalogEntry("operationsLabrumAndInstability", "glenoidBoneGraftProcedure")}>Glenoid bone graft procedure</button>
                <button onClick={() => addCatalogEntry("operationsLabrumAndInstability", "humeralBoneGraftProcedure")}>Humeral bone graft procedure</button>
                {/* <button onClick={() => addCatalogEntry("operationsLabrumAndInstability", "dynamicAnteriorStabilization")}>Dynamic Anterior Stabilization (DAS)</button> */}
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="6" className="grafts">
              <Accordion.Header>Grafts</Accordion.Header>
              <Accordion.Body>
                <button onClick={() => addCatalogEntry("grafts", "boneGraftAutografts")}>Bone graft - autografts</button>
                <button onClick={() => addCatalogEntry("grafts", "boneGraftAllografts")}>Bone graft - allografts</button>
                <button onClick={() => addCatalogEntry("grafts", "boneGraftSyntheticBoneVoidFillers")}>Bone graft - synthetic bone void fillers</button>
                <button onClick={() => addCatalogEntry("grafts", "cartilageGraftChondralGrafts")}>Cartilage graft - chondral grafts</button>
                <button onClick={() => addCatalogEntry("grafts", "cartilageGraftOsteochondralGrafts")}>Cartilage graft - osteochondral grafts</button>
                <button onClick={() => addCatalogEntry("grafts", "softTissueTendonGrafts")}>Soft tissue - tendon grafts</button>
                <button onClick={() => addCatalogEntry("grafts", "softTissueAugmentationGrafts")}>Soft tissue - augmentation grafts</button>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="7" className="implants">
              <Accordion.Header>Implants</Accordion.Header>
              <Accordion.Body>
                <button onClick={() => addCatalogEntry("implants", "acJointStabilization")}>AC joint stabilization</button>
                <button onClick={() => addCatalogEntry("implants", "biceps")}>Biceps</button>
                <button onClick={() => addCatalogEntry("implants", "capsularRepairHaglAlpsaRemplissageAsa")}>Capsular repair HAGL/ALPSA/remplissage/ASA</button>
                <button onClick={() => addCatalogEntry("implants", "chondral")}>Chondral</button>
                <button onClick={() => addCatalogEntry("implants", "fractureManagement")}>Fracture management (ORIF)</button>
                <button onClick={() => addCatalogEntry("implants", "glenoidBoneGraftProcedure")}>Glenoid bone graft procedure</button>
                <button onClick={() => addCatalogEntry("implants", "labralRepair")}>Labral repair</button>
                <button onClick={() => addCatalogEntry("implants", "osAcromiale")}>Os acromiale</button>
                <button onClick={() => addCatalogEntry("implants", "osteotomy")}>Osteotomy</button>
                <button onClick={() => addCatalogEntry("implants", "pastaRepairKit")}>Pasta repair kit</button>
                <button onClick={() => addCatalogEntry("implants", "pecMajor")}>Pec major</button>
                <button onClick={() => addCatalogEntry("implants", "rotatorCuffImplantsLateralRow")}>Rotator cuff implants - lateral row (lateral half of greater tuberosity)</button>
                <button onClick={() => addCatalogEntry("implants", "rotatorCuffImplantsMedialRow")}>Rotator cuff implants - medial row (medial half of greater tuberosity)</button>
                <button onClick={() => addCatalogEntry("implants", "cuffMend")}>Cuff Mend</button>
                <button onClick={() => addCatalogEntry("implants", "slap")}>SLAP</button>
                <button onClick={() => addCatalogEntry("implants", "subscap")}>Subscap</button>
                <button onClick={() => addCatalogEntry("implants", "superiorCapsuleReconstructionGlenoid")}>Superior Capsule Reconstruction (SCR) - glenoid</button>
                <button onClick={() => addCatalogEntry("implants", "superiorCapsuleReconstructionHumeralSideLateralRow")}>Superior Capsule Reconstruction (SCR) - humeral side lateral Row</button>
                <button onClick={() => addCatalogEntry("implants", "superiorCapsuleReconstructionHumeralSideMedialRow")}>Superior Capsule Reconstruction (SCR) - humeral side medial Row</button>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="8" className="surgicalDressing">
              <Accordion.Header>Surgical Dressing</Accordion.Header>
              <Accordion.Body>
                <button onClick={() => addCatalogEntry("surgicalDressing")}>Sling/Brace application</button>
              </Accordion.Body>
            </Accordion.Item>
          </>
        )
      }
      case "shoulderArthroplasty": {
        return (
          <>
            <Accordion.Item eventKey="3" className="diagnosis">
              <Accordion.Header>Diagnosis</Accordion.Header>
              <Accordion.Body>
                <button onClick={() => addCatalogEntry("diagnosis", "arthritis")}>Arthritis</button>
                <button onClick={() => addCatalogEntry("diagnosis", "rotatorCuffArthropathy")}>Rotator Cuff Arthropathy</button>
                <button onClick={() => addCatalogEntry("diagnosis", "failedRotatorCuffRepair")}>Failed Rotator Cuff Repair</button>
                <button onClick={() => addCatalogEntry("diagnosis", "failedShoulderArthroplasty")}>Failed Shoulder Arthroplasty</button>
                <button onClick={() => addCatalogEntry("diagnosis", "clavicleFracture")}>Clavicle Fracture</button>
                <button onClick={() => addCatalogEntry("diagnosis", "humeralFracture")}>Humeral Fracture</button>
                <button onClick={() => addCatalogEntry("diagnosis", "scapulaFracture")}>Scapula Fracture</button>
                <button onClick={() => addCatalogEntry("diagnosis", "chronicRotatorCuffTear")}>Chronic Rotator Cuff Tear</button>
                <button onClick={() => addCatalogEntry("diagnosis", "avn")}>AVN</button>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="4" className="operations">
              <Accordion.Header>Operations/Implants</Accordion.Header>
              <Accordion.Body>
                <button onClick={() => addCatalogEntry("operationsAndImplants", "approachSupscapRepairAndApplicationBraces")}>Approach, Supscap Repair and Application/Braces</button>
                <button onClick={() => addCatalogEntry("operationsAndImplants", "hemiarthroplasty")}>Hemiarthroplasty</button>
                <button onClick={() => addCatalogEntry("operationsAndImplants", "humeralHeadResurfacing")}>Humeral Head Resurfacing</button>
                <button onClick={() => addCatalogEntry("operationsAndImplants", "biologicTotalShoulder")}>Biologic Total Shoulder</button>
                <button onClick={() => addCatalogEntry("operationsAndImplants", "totalShoulderReplacement")}>Total Shoulder Replacement</button>
                <button onClick={() => addCatalogEntry("operationsAndImplants", "inverseReverseTotalShoulderReplacement")}>Inverse/Reverse Total Shoulder Replacement</button>
                <button onClick={() => addCatalogEntry("operationsAndImplants", "superiorCapsuleReconstruction")}>Superior Capsule Reconstruction (SCR)</button>
                <button onClick={() => addCatalogEntry("operationsAndImplants", "clavicleFracture")}>Clavicle Fracture</button>
                <button onClick={() => addCatalogEntry("operationsAndImplants", "greaterTuberosityFracture")}>Greater Tuberosity Fracture</button>
                <button onClick={() => addCatalogEntry("operationsAndImplants", "proximalHumerusFracture")}>Proximal Humerus Fracture</button>
                <button onClick={() => addCatalogEntry("operationsAndImplants", "humeralShaftFracture")}>Humeral Shaft Fracture</button>
                <button onClick={() => addCatalogEntry("operationsAndImplants", "glenoidFracture")}>Glenoid Fracture</button>
                <button onClick={() => addCatalogEntry("operationsAndImplants", "fusion")}>Fusion</button>
                <button onClick={() => addCatalogEntry("operationsAndImplants", "prostalac")}>Prostalac</button>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="5" className="grafts">
              <Accordion.Header>Grafts</Accordion.Header>
              <Accordion.Body>
                <button onClick={() => addCatalogEntry("grafts", "boneGraftAutografts")}>Bone graft - autografts</button>
                <button onClick={() => addCatalogEntry("grafts", "boneGraftAllografts")}>Bone graft - allografts</button>
                <button onClick={() => addCatalogEntry("grafts", "boneGraftSyntheticBoneVoidFillers")}>Bone graft - synthetic bone void fillers</button>
                <button onClick={() => addCatalogEntry("grafts", "cartilageGraftChondralGrafts")}>Cartilage graft - chondral grafts</button>
                <button onClick={() => addCatalogEntry("grafts", "cartilageGraftOsteochondralGrafts")}>Cartilage graft - osteochondral grafts</button>
                <button onClick={() => addCatalogEntry("grafts", "softTissueMeniscalAllograftFasciaLata")}>Soft Tissue - Meniscal Allograft, Fascia Lata, etc</button>
                <button onClick={() => addCatalogEntry("grafts", "softTissueAugmentationGrafts")}>Soft tissue - augmentation grafts</button>
                <button onClick={() => addCatalogEntry("grafts", "softTissueAdiposeTissueGraft")}>Soft Tissue - Adipose Tissue Graft (Autograft)</button>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="6" className="postOperativeManagement">
              <Accordion.Header>Post Operative Management</Accordion.Header>
              <Accordion.Body>
                <button onClick={() => addCatalogEntry("postOperativeManagement")}>Post-Op Path</button>
              </Accordion.Body>
            </Accordion.Item>
          </>
        )
      }
      case "elbow": {
        return (
          <>
            <Accordion.Item eventKey="3" className="diagnosis">
              <Accordion.Header>Diagnosis</Accordion.Header>
              <Accordion.Body>
                <button onClick={() => addCatalogEntry("diagnosis", "tendon")}>Tendon</button>
                <button onClick={() => addCatalogEntry("diagnosis", "elbowLigamentInstability")}>Ligament/Instability</button>
                <button onClick={() => addCatalogEntry("diagnosis", "elbowOsteochondralLesion")}>Osteochondral Lesion</button>
                <button onClick={() => addCatalogEntry("diagnosis", "elbowOsteoarthritis")}>Osteoarthritis</button>
                <button onClick={() => addCatalogEntry("diagnosis", "elbowOtherInflammatoryArthritis")}>Other Inflammatory Arthritis</button>
                <button onClick={() => addCatalogEntry("diagnosis", "elbowLooseBody")}>Loose Body(s)</button>
                <button onClick={() => addCatalogEntry("diagnosis", "elbowCapsuleSynovium")}>Capsule/Synovium</button>
                <button onClick={() => addCatalogEntry("diagnosis", "elbowNerve")}>Nerve</button>
                <button onClick={() => addCatalogEntry("diagnosis", "elbowFracture")}>Fracture</button>
                <button onClick={() => addCatalogEntry("diagnosis", "olecranonSpur")}>Olecranon Spur</button>
                <button onClick={() => addCatalogEntry("diagnosis", "elbowOther")}>Other</button>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="4" className="operations">
              <Accordion.Header>Operations</Accordion.Header>
              <Accordion.Body>
                <button onClick={() => addCatalogEntry("operations", "elbowCaseDetails")}>Case Details</button>
                <button onClick={() => addCatalogEntry("operations", "elbowTendonProcedures")}>Tendon Procedures</button>
                <button onClick={() => addCatalogEntry("operations", "elbowCollateralLigaments")}>Collateral Ligaments</button>
                <button onClick={() => addCatalogEntry("operations", "elbowCartilageTreatment")}>Cartilage Treatment</button>
                <button onClick={() => addCatalogEntry("operations", "elbowOsteochondralTreatment")}>Osteochondral Treatment</button>
                <button onClick={() => addCatalogEntry("operations", "elbowOsteophyteDebridementExcision")}>Osteophyte Debridement/Excision</button>
                <button onClick={() => addCatalogEntry("operations", "elbowRemovalLooseBody")}>Removal of Loose Body(s)</button>
                <button onClick={() => addCatalogEntry("operations", "elbowCapsuleSynovium")}>Capsule/Synovium</button>
                <button onClick={() => addCatalogEntry("operations", "elbowNerve")}>Nerve</button>
                <button onClick={() => addCatalogEntry("operations", "elobwOrif")}>ORIF</button>
                <button onClick={() => addCatalogEntry("operations", "radialHeadExcision")}>Radial Head Excision</button>
                <button onClick={() => addCatalogEntry("operations", "radialHeadImplant")}>Radial Head Implant</button>
                <button onClick={() => addCatalogEntry("operations", "totalElbowArthroplasty")}>Total Elbow Arthroplasty</button>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="5" className="grafts">
              <Accordion.Header>Grafts</Accordion.Header>
              <Accordion.Body>
                <button onClick={() => addCatalogEntry("grafts", "boneGraftAutografts")}>Bone graft - Autografts</button>
                <button onClick={() => addCatalogEntry("grafts", "boneGraftAllografts")}>Bone graft - allografts</button>
                <button onClick={() => addCatalogEntry("grafts", "boneGraftFreshAllografts")}>Bone graft - fresh allografts</button>
                <button onClick={() => addCatalogEntry("grafts", "boneGraftSyntheticBoneVoidFillers")}>Bone graft - synthetic bone void fillers</button>
                <button onClick={() => addCatalogEntry("grafts", "cartilageGraftChondralGrafts")}>Cartilage graft - chondral grafts</button>
                <button onClick={() => addCatalogEntry("grafts", "cartilageGraftOsteochondralGrafts")}>Cartilage graft - osteochondral grafts</button>
                <button onClick={() => addCatalogEntry("grafts", "elbowSoftTissueTendonGrafts")}>Soft tissue - tendon grafts</button>
                <button onClick={() => addCatalogEntry("grafts", "softTissueAugmentationGrafts")}>Soft tissue - augmentation grafts</button>
                <button onClick={() => addCatalogEntry("grafts", "softTissueAdiposeTissueGraft")}>Soft tissue - adipose tissue graft (autograft)</button>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="6" className="implants">
              <Accordion.Header>Implants</Accordion.Header>
              <Accordion.Body>
                <button onClick={() => addCatalogEntry("implants", "elbowTendonProcedures")}>Tendon Procedures</button>
                <button onClick={() => addCatalogEntry("implants", "elbowCollateralLigaments")}>Collateral Ligaments</button>
                <button onClick={() => addCatalogEntry("implants", "elbowCartilageTreatment")}>Cartilage Treatment</button>
                <button onClick={() => addCatalogEntry("implants", "elbowOsteochondralTreatment")}>Osteochondral Treatment</button>
                <button onClick={() => addCatalogEntry("implants", "elbowOrif")}>ORIF</button>
                <button onClick={() => addCatalogEntry("implants", "elbowRadialHeadImplant")}>Radial Head Implant</button>
                <button onClick={() => addCatalogEntry("implants", "elbowTotalElbowArthroplasty")}>Total Elbow Arthroplasty</button>
              </Accordion.Body>
            </Accordion.Item>
          </>
        )
      }
      default: return
    }
  }

  return (
    <div className="analysis">
      {loading ? (<Oval
          height={80}
          width={80}
          color="#4caeff"
          wrapperStyle={{}}
          wrapperClass="loader"
          visible={true}
          ariaLabel='oval-loading'
          secondaryColor="#4caeff"
          strokeWidth={2}
          strokeWidthSecondary={2}
        />
        ) : (
        <>
          <Modal className={"analysis-modal"} show={showModal} onHide={() => setShowModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>
                <div>New Patient Group</div>
                <div>{cases} cases</div>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form onSubmit={e => {e.preventDefault(); savePatientGroupHandler()}}>
                <Form.Group className='group-study'>
                  <Form.Label><h4>Study</h4></Form.Label>
                  <Form.Select
                        value={study}
                        onChange={(e) => {setStudy(e.target.value)}}
                      >
                        <option value="" hidden>Select an option</option>
                        <option value="shoulderArthroplasty">Shoulder Arthroplasty</option>
                        <option value="elbow">Elbow</option>
                        <option value="shoulderArthroscopy">Shoulder Arthroscopy</option>
                      </Form.Select>
                </Form.Group>
                <Form.Group className='group-name'>
                  <Form.Label><h4>Group Name</h4></Form.Label>
                  <Form.Control value={groupName} type="text" onChange={(e) => setGroupName(e.target.value)}/>
                </Form.Group>
              </Form>
              
              <div className='divider'></div>

              {study !== "" ? 
                (
                  <div className="analysis-group-metadata">
                    <div className="analysis-group-metadata-overview">
                      <h4>Catalog</h4>
                      <Accordion>
                        <Accordion.Item eventKey="0" className="patient-information-and-surgical-time-and-personnel">
                          <Accordion.Header>Patient information</Accordion.Header>
                          <Accordion.Body>
                            <button onClick={() => addCatalogEntry("patientInformationSurgicalTimeAndPersonnel", "patientInformationForAnalysis")}>Patient information</button>
                          </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item eventKey="1" className="hand-dominance">
                          <Accordion.Header>Hand dominance</Accordion.Header>
                          <Accordion.Body>
                            <button onClick={() => addCatalogEntry("handDominance")}>Hand dominance</button>
                          </Accordion.Body>
                        </Accordion.Item>
                        {
                          renderCatalog()
                        }
                      </Accordion>
                    </div>
                    <div className="analysis-group-metadata-details">
                      <div className="analysis-group-metadata-details-header">
                        <h4>Forms</h4>
                      </div>
                      {catalogEntries && catalogEntries.map((catalogEntry, index) => {
                        const FormComponent = (() => {
                          switch(catalogEntry.category) {
                            case "patientInformationSurgicalTimeAndPersonnel": return PatientInformationSurgicalTimeAndPersonnel;
                            case "handDominance": return HandDominance;
                            case "diagnosis": return Diagnosis;
                            case "operations": return Operations;
                            case "operationsLabrumAndInstability": return OperationsLabrumAndInstability;
                            case "implants": return Implants;
                            case "surgicalDressing": return SurgicalDressing;
                            case "grafts": return Grafts;
                            case "operationsAndImplants": return OperationsAndImplants;
                            case "postOperativeManagement": return PostOperativeManagement;
                            default: return null;
                          }
                        })();

                        // --- MODIFICATION: Determine if the buttons should be shown ---
                        const showToggleButtons = catalogEntry.category !== 'patientInformationSurgicalTimeAndPersonnel' && catalogEntry.category !== 'handDominance';

                        // Helper function to get category class name
                        const getCategoryClassName = (category) => {
                          switch(category) {
                            case 'patientInformationSurgicalTimeAndPersonnel': return 'patient-information';
                            case 'handDominance': return 'hand-dominance';
                            case 'diagnosis': return 'diagnosis';
                            case 'operations': return 'operations';
                            case 'operationsLabrumAndInstability': return 'operations-labrum-instability';
                            case 'operationsAndImplants': return 'operations-and-implants';
                            case 'implants': return 'implants';
                            case 'surgicalDressing': return 'surgical-dressing';
                            case 'grafts': return 'grafts';
                            case 'postOperativeManagement': return 'post-operative-management';
                            case 'complications': return 'complications';
                            default: return '';
                          }
                        };

                        // Determine closing direction:
                        // If opening an accordion below (higher index), close from bottom
                        // If opening an accordion above (lower index), close from top
                        const closeFromBottom = activeAccordionIndex !== null && index < activeAccordionIndex;

                        return (
                          // Apply category-specific class along with include/exclude class
                          <div key={index} className={`form-container ${getCategoryClassName(catalogEntry.category)} ${showToggleButtons ? catalogEntry.filterType : ''}`}>
                            <div className="form-content">
                              {FormComponent && <FormComponent
                                form={catalogEntry.form}
                                formData={catalogEntry.formData}
                                arrayIndex={index}
                                onChangeHandler={handleCatalogEntryFormDataUpdate}
                                onRemoveHandler={handleCatalogEntryRemoval}
                                study={study}
                                showToggleButtons={showToggleButtons}
                                filterType={catalogEntry.filterType}
                                onFilterTypeChange={(filterType) => handleFilterTypeChange(index, filterType)}
                                activeKey={activeAccordion}
                                onAccordionChange={(form) => handleAccordionChange(form, index)}
                                closeFromBottom={closeFromBottom}
                              />}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>   
                ) : null
              }
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button variant="success" disabled={study === ""? true : false} onClick={() => savePatientGroupHandler()}>
                Save Patient Group
              </Button>
            </Modal.Footer>
          </Modal>
          <div className="analysis-header">
            <div className="header-content">
              <div className="title-section">
                <h1 className="page-title">
                  <FaChartLine className="title-icon" />
                  Analysis
                </h1>
                <p className="page-subtitle">Compare patient groups and analyze outcomes</p>
              </div>
              <div className="action-section">
                <Button
                  variant="primary"
                  className="create-group-btn"
                  onClick={() => {setEditingGroupIndex(null); setShowModal(true)}}
                >
                  <FaPlus className="btn-icon" />
                  Create Group
                </Button>
              </div>
            </div>
          </div>
          <div className="analysis-content">
            {patientGroups.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <FaChartLine />
                </div>
                <h3>No patient groups created yet</h3>
                <p>Create your first patient group to start analyzing outcomes and comparing different cohorts.</p>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => {setEditingGroupIndex(null); setShowModal(true)}}
                >
                  <FaPlus className="btn-icon" />
                  Create Your First Group
                </Button>
              </div>
            ) : (
              <>
                <div className="analysis-table-section">
                  <div className="section-header">
                    <h2>Patient Groups</h2>
                    <span className="group-count">{patientGroups.length} group{patientGroups.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="table-container">
                    <Table className="modern-table" hover>
                      <thead>
                        <tr>
                          <th className='group-name'>Group Name</th>
                          <th className='study'>Study</th>
                          <th className='cases'>Cases</th>
                          <th className='filters'>Filters Applied</th>
                          <th className='actions'>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {patientGroups.map((patientGroup, index) => (
                          <tr key={patientGroup.groupName} className="group-row">
                            <td className="group-name-cell">
                              <div className="group-info">
                                <div className="group-color-indicator" style={{ backgroundColor: patientGroup.color }}></div>
                                <div className="group-details">
                                  <span className="group-name">{patientGroup.groupName}</span>
                                </div>
                              </div>
                            </td>
                            <td className="study-cell">
                              <span className="study-badge">{patientGroup.study}</span>
                            </td>
                            <td className="cases-cell">
                              <span className="cases-count">{patientGroup.cases}</span>
                            </td>
                            <td className="filters-cell">
                              <div className="filters-container">
                                {patientGroup.catalogEntries.map((catalogEntry, catalogEntryIndex) => {
                                  const isFilterable = catalogEntry.category !== 'patientInformationSurgicalTimeAndPersonnel' && catalogEntry.category !== 'handDominance';
                                  return (
                                    <div key={catalogEntryIndex} className={`filter-item ${isFilterable ? catalogEntry.filterType : ''}`}>
                                      {isFilterable && (
                                        <span className="filter-type-badge">
                                          {catalogEntry.filterType === 'include' ? 'INC' : 'EXC'}
                                        </span>
                                      )}
                                      <div className="filter-content">
                                        <div className="filter-category">
                                          {camelCaseToReadable(catalogEntry.category)} - {camelCaseToReadable(catalogEntry.form)}
                                        </div>
                                        <div className="filter-attributes">
                                          {Object.entries(catalogEntry.formData).map(([key, value]) => {
                                            const isActiveFilter =
                                              (typeof value === 'boolean' && value === true) ||
                                              (typeof value !== 'boolean' && value !== null && value !== '' && value !== undefined);

                                            if (isActiveFilter) {
                                              return (
                                                <span key={key} className="filter-tag">
                                                  {camelCaseToReadable(key)}: {String(value)}
                                                </span>
                                              );
                                            }
                                            return null;
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </td>
                            <td className="actions-cell">
                              <div className='action-buttons'>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => {setEditingGroupIndex(index); editPatientGroupHandler(index)}}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => {setPatientGroups(prevState => prevState.filter((_, i) => i !== index)); setNumberOfGroups(prevState => prevState - 1)}}
                                >
                                  Remove
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </>
            )}
          </div>
          {patientGroups.length > 0 && (
            <div className="analysis-charts-section">
              <div className="section-header">
                <h2>Outcome Analysis</h2>
                <p className="section-subtitle">Compare patient group outcomes across different metrics</p>
              </div>
              <div className="charts-grid">
                <div className="chart-container" style={{'--animation-order': 0}}>
                  <div className="chart-header">
                    <h3>VAS Pain Score</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={500}>
                <ComposedChart {...getCommonChartProps()}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#f0f4f8" strokeWidth={1} />
                  <XAxis {...getResponsiveXAxisProps()} />
                  <YAxis
                    label={{ value: 'VAS Score', angle: -90, offset:20, position:'insideBottomLeft'}}
                    domain={[0, 10]}
                    tickCount={10}
                    tick={{ fill: '#718096', fontSize: 12, fontWeight: 500 }}
                    axisLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                    tickLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                  />
                  <Tooltip
                    content={<CustomTooltip chartType="VAS Pain Score" />}
                    animationDuration={300}
                    animationEasing="ease-out"
                    position={{ x: 'auto', y: 'auto' }}
                    allowEscapeViewBox={{ x: false, y: false }}
                    wrapperStyle={{ zIndex: 1000 }}
                  />
                  <Legend {...getResponsiveLegendProps()} />
                  {
                    patientGroups.map((patientGroup, index) => (
                      <Line
                        type="monotone"
                        key={`line-${index}`}
                        data={patientGroup.metrics.vasData}
                        dataKey="score"
                        stroke={patientGroup.color}
                        strokeWidth={3}
                        dot={{ r: 5, strokeWidth: 3, fill: patientGroup.color, stroke: '#fff' }}
                        activeDot={{
                          r: 8,
                          strokeWidth: 4,
                          fill: patientGroup.color,
                          stroke: '#fff',
                          filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))'
                        }}
                        name={patientGroup.groupName + ` (n=${patientGroup.cases})`}
                        animationBegin={600 + index * 200}
                        animationDuration={1200}
                      >
                        <LabelList
                          key={`label-${index}`}
                          dataKey="score"
                          content={renderCustomLabel(400, { fill: patientGroup.color })}
                        />
                      </Line>
                    ))
                  }
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-container" style={{'--animation-order': 1}}>
                  <div className="chart-header">
                    <h3>SANE Score</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={500}>
                <ComposedChart {...getCommonChartProps()}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#f0f4f8" strokeWidth={1} />
                  <XAxis
                    dataKey="stage"
                    angle={45}
                    textAnchor="start"
                    interval={0}
                    dy={10}
                    allowDuplicatedCategory={false}
                    tick={{ fill: '#718096', fontSize: 12, fontWeight: 500 }}
                    axisLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                    tickLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                  />
                  <YAxis
                    label={{ value: 'Sane Score', angle: -90, offset:10, position:'insideBottomLeft'}}
                    domain={[0, 100]}
                    tickCount={10}
                    tick={{ fill: '#718096', fontSize: 12, fontWeight: 500 }}
                    axisLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                    tickLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                  />
                  <Tooltip
                    content={<CustomTooltip chartType="SANE Score" />}
                    animationDuration={300}
                    animationEasing="ease-out"
                    position={{ x: 'auto', y: 'auto' }}
                    allowEscapeViewBox={{ x: false, y: false }}
                    wrapperStyle={{ zIndex: 1000 }}
                  />
                  <Legend {...getResponsiveLegendProps()} />
                  {
                    patientGroups.map((patientGroup, index) => (
                      <Line
                        type="monotone"
                        key={`line-${index}`}
                        data={patientGroup.metrics.saneData}
                        dataKey="score"
                        stroke={patientGroup.color}
                        strokeWidth={3}
                        dot={{ r: 5, strokeWidth: 3, fill: patientGroup.color, stroke: '#fff' }}
                        activeDot={{
                          r: 8,
                          strokeWidth: 4,
                          fill: patientGroup.color,
                          stroke: '#fff',
                          filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))'
                        }}
                        name={patientGroup.groupName + ` (n=${patientGroup.cases})`}
                        animationBegin={600 + index * 200}
                        animationDuration={1200}
                      >
                        <LabelList
                          key={`label-${index}`}
                          dataKey="score"
                          content={renderCustomLabel(400, { fill: patientGroup.color })}
                        />
                      </Line>
                    ))
                  }
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                {patientGroups[0]?.study === "elbow" && (
                  <div className="chart-container" style={{'--animation-order': 2}}>
                    <div className="chart-header">
                      <h3>ASES Elbow Score</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={500}>
                <ComposedChart {...getCommonChartProps()}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#f0f4f8" strokeWidth={1} />
                  <XAxis
                    dataKey="stage"
                    angle={45}
                    textAnchor="start"
                    interval={0}
                    dy={10}
                    allowDuplicatedCategory={false}
                    tick={{ fill: '#718096', fontSize: 12, fontWeight: 500 }}
                    axisLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                    tickLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                  />
                  <YAxis
                    label={{ value: 'ASES Elbow Score', angle: -90, offset:10, position:'insideBottomLeft'}}
                    domain={[0, 36]}
                    tickCount={6}
                    tick={{ fill: '#718096', fontSize: 12, fontWeight: 500 }}
                    axisLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                    tickLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                  />
                  <Tooltip
                    content={<CustomTooltip chartType="ASES Elbow Score" />}
                    animationDuration={300}
                    animationEasing="ease-out"
                    position={{ x: 'auto', y: 'auto' }}
                    allowEscapeViewBox={{ x: false, y: false }}
                    wrapperStyle={{ zIndex: 1000 }}
                  />
                  <Legend {...getResponsiveLegendProps()} />
                  {
                    patientGroups.map((patientGroup, index) => (
                      <Line
                        type="monotone"
                        key={`line-${index}`}
                        data={patientGroup.metrics.asesElbowData}
                        dataKey="score"
                        stroke={patientGroup.color}
                        strokeWidth={3}
                        dot={{ r: 5, strokeWidth: 3, fill: patientGroup.color, stroke: '#fff' }}
                        activeDot={{
                          r: 8,
                          strokeWidth: 4,
                          fill: patientGroup.color,
                          stroke: '#fff',
                          filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))'
                        }}
                        name={patientGroup.groupName + ` (n=${patientGroup.cases})`}
                      >
                        <LabelList
                          key={`label-${index}`}
                          dataKey="score"
                          content={renderCustomLabel(400, { fill: patientGroup.color })}
                        />
                      </Line>
                    ))
                  }
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {patientGroups[0]?.study !== "elbow" && (
                  <div className="chart-container" style={{'--animation-order': 2}}>
                    <div className="chart-header">
                      <h3>ASES Function Score</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={500}>
                <ComposedChart {...getCommonChartProps()}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#f0f4f8" strokeWidth={1} />
                  <XAxis
                    dataKey="stage"
                    angle={45}
                    textAnchor="start"
                    interval={0}
                    dy={10}
                    allowDuplicatedCategory={false}
                    tick={{ fill: '#718096', fontSize: 12, fontWeight: 500 }}
                    axisLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                    tickLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                  />
                  <YAxis
                    label={{ value: patientGroups[0].study === "elbow"? 'ASES Elbow Score' : 'ASES Function Score', angle: -90, offset:10, position:'insideBottomLeft'}}
                    domain={patientGroups[0].study === "elbow"? [0, 36] : [0, 30]}
                    tickCount={6}
                    tick={{ fill: '#718096', fontSize: 12, fontWeight: 500 }}
                    axisLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                    tickLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                  />
                  <Tooltip
                    content={<CustomTooltip chartType={patientGroups[0].study === "elbow"? "ASES Elbow Score" : "ASES Function Score"} />}
                    animationDuration={300}
                    animationEasing="ease-out"
                    position={{ x: 'auto', y: 'auto' }}
                    allowEscapeViewBox={{ x: false, y: false }}
                    wrapperStyle={{ zIndex: 1000 }}
                  />
                  <Legend {...getResponsiveLegendProps()} />
                  {
                    patientGroups.map((patientGroup, index) => (
                      <Line
                        type="monotone"
                        key={`line-${index}`}
                        data={patientGroup.study === "elbow"? patientGroup.metrics.asesElbowData : patientGroup.metrics.asesFunctionData}
                        dataKey={patientGroup.study === "elbow"? "score" : "functionScore"}
                        stroke={patientGroup.color}
                        strokeWidth={3}
                        dot={{ r: 5, strokeWidth: 3, fill: patientGroup.color, stroke: '#fff' }}
                        activeDot={{
                          r: 8,
                          strokeWidth: 4,
                          fill: patientGroup.color,
                          stroke: '#fff',
                          filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))'
                        }}
                        name={patientGroup.groupName + ` (n=${patientGroup.cases})`}
                      >
                        <LabelList
                          key={`label-${index}`}
                          dataKey="functionScore"
                          content={renderCustomLabel(400, { fill: patientGroup.color })}
                        />
                      </Line>
                    ))
                  }
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {patientGroups[0]?.study !== "elbow" && (
                  <div className="chart-container" style={{'--animation-order': 3}}>
                    <div className="chart-header">
                      <h3>ASES Index Score</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={500}>
                <ComposedChart {...getCommonChartProps()}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#f0f4f8" strokeWidth={1} />
                  <XAxis
                    dataKey="stage"
                    angle={45}
                    textAnchor="start"
                    interval={0}
                    dy={10}
                    allowDuplicatedCategory={false}
                    tick={{ fill: '#718096', fontSize: 12, fontWeight: 500 }}
                    axisLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                    tickLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                  />
                  <YAxis
                    label={{ value: 'ASES Index Score', angle: -90, offset:10, position:'insideBottomLeft'}}
                    domain={[0, 100]}
                    tickCount={10}
                    tick={{ fill: '#718096', fontSize: 12, fontWeight: 500 }}
                    axisLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                    tickLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                  />
                  <Tooltip
                    content={<CustomTooltip chartType="ASES Index Score" />}
                    animationDuration={300}
                    animationEasing="ease-out"
                    position={{ x: 'auto', y: 'auto' }}
                    allowEscapeViewBox={{ x: false, y: false }}
                    wrapperStyle={{ zIndex: 1000 }}
                  />
                  <Legend {...getResponsiveLegendProps()} />
                  {
                    patientGroups.map((patientGroup, index) => (
                      <Line
                        type="monotone"
                        key={`line-${index}`}
                        data={patientGroup.metrics.asesIndexData}
                        dataKey="indexScore"
                        stroke={patientGroup.color}
                        strokeWidth={3}
                        dot={{ r: 5, strokeWidth: 3, fill: patientGroup.color, stroke: '#fff' }}
                        activeDot={{
                          r: 8,
                          strokeWidth: 4,
                          fill: patientGroup.color,
                          stroke: '#fff',
                          filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))'
                        }}
                        name={patientGroup.groupName + ` (n=${patientGroup.cases})`}
                      >
                        <LabelList
                          key={`label-${index}`}
                          dataKey="indexScore"
                          content={renderCustomLabel(400, { fill: patientGroup.color })}
                        />
                      </Line>
                    ))
                  }
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Analysis